import React from 'react';
import styled from '@emotion/styled';
import { TableEvent } from '@baemingo/table-events';
import moment from 'moment';
import _ from 'lodash';

import {
  JournalCompany_company_staffs as Staff,
  JournalCompany_company_items as Item,
  JournalCompany_company_venues_tables as Table,
} from '../__queries__';

const Wrapper = styled.div``;

const InnerWrapper = styled.div`
  flex: 1;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.5);
  border-radius: 5px;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  box-sizing: border-box;
`;

const TableHeader = styled.div`
  display: flex;
  background-color: #fbfbfb;
  align-items: center;
  border-bottom: 1px solid #e8e9ea;
`;

const Body = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

const Row = styled.div`
  background-color: #fff;
  width: 100%;
  display: flex;
  box-sizing: border-box;
  position: relative;
  transition: box-shadow 2s ease-in-out;
  border: 1px solid;
  border-color: #e8e9ea;
  margin-top: -1px;
  transition: border-color 0.2s ease-in-out;

  &:nth-of-type(2n) {
    background-color: #fbfbfb;
  }

  div {
    font-weight: 400;
  }
`;

const Title = styled.div`
  flex: 1;
  color: #41474f;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 15px;
  padding: 9px 12px;
  box-sizing: border-box;
`;

const QtyTitle = styled.div`
  width: 116px;
  text-align: center;
  color: #41474f;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 15px;
  padding: 9px 8px;
  box-sizing: border-box;
`;

const Product = styled.div`
  flex: 1;
  padding: 7px 12px;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.24px;
  color: #1c232c;
  line-height: 20px;
  box-sizing: border-box;
  word-break: break-word;
`;

const Quantity = styled.div`
  padding: 7px 4px;
  width: 116px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  border-left: 1px solid;
  border-color: #e8e9ea;
  position: relative;
  color: #1c232c;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  transition: border-color 0.2s ease-in-out;
`;

interface Props {
  headers: string[];
  events: TableEvent[];
  staff: Staff[];
  items: Item[];
  tables: Table[];
}

const TableTable: React.FunctionComponent<Props> = ({ headers, events, staff, items, tables }) => {
  const expandTableEvent = (event: TableEvent): (string | number | Function)[] => {
    const itemName = (id: string) => items.find((t) => t.id === id)?.title ?? '--';
    const tableName = (id: string) => tables.find((t) => t.id === id)?.code ?? '--';
    const staffName = staff.find((s) => s.id === event.staffId)?.name ?? '--';
    const name = (str: string) => str.substr(0, 8);

    switch (event.type) {
      case 'ADD_ITEM':
        return ['Add item', name(event.chequeId), event.title, staffName];
      case 'ADD_PAYMENT':
        return ['Payment', name(event.chequeId), event.amount / 100, staffName];
      case 'MERGE_CHEQUE':
        return ['Merge cheques', name(event.id), 'With ' + name(event.targetId), staffName];
      case 'NAME_CHEQUE':
        return ['Name cheque', name(event.id), event.name || '', staffName];
      case 'MOVE_ITEM':
        return ['Move item', itemName(event.id), 'To ' + name(event.toCheque), staffName];
      case 'MOVE_CHEQUE':
        return ['Move cheque', name(event.id), tableName(event.tableId || ''), staffName];
      case 'SPLIT_CHEQUE':
        return ['Split cheque', name(event.id), '', staffName];
      case 'CREATE_CHEQUE':
        return [
          'Create cheque',
          name(event.id),
          () => <span>Table {tableName(event.tableId || '') ?? '--'}</span>,
          staffName,
        ];
      case 'REMOVE_ITEM':
        return ['Remove item', name(event.id), '', staffName];
      case 'REMOVE_CHEQUE':
        return ['Remove cheque', name(event.id), '', staffName];
      case 'CHEQUE_PRINTED':
        return ['Cheque bill printe', '', '', staffName];
      case 'ADD_NOTE':
        return ['Add note', name(event.id), 'Table ' + tableName(event.tableId || ''), staffName];
      case 'REMOVE_NOTE':
        return [
          'Remove note',
          name(event.id),
          'Table ' + tableName(event.tableId || ''),
          staffName,
        ];
      case 'SET_CHEQUE_COVERAGE':
        return ['Set cheque coverage', name(event.id), tableName(event.eventId || ''), staffName];
      case 'SET_DISCOUNT_ON_ITEMS':
        return ['Set discount on items', '', tableName(event.eventId || ''), staffName];
    }

    return [event.type, '', '', staffName];
  };

  return (
    <Wrapper>
      <InnerWrapper>
        <TableHeader>
          {headers.map((header, i) => {
            if (headers.length - 1 !== i) {
              return <Title key={header}>{header}</Title>;
            }
            return <QtyTitle key={header}>{header}</QtyTitle>;
          })}
        </TableHeader>
        <Body>
          {events.map((event) => (
            <Row key={event.eventId}>
              {expandTableEvent(event).map((cell, i) => (
                <Product key={event.eventId + i}>
                  {typeof cell === 'function' ? cell() : cell}
                </Product>
              ))}
              <Quantity>{moment(event.createdAt).format('MM/DD HH:mm')}</Quantity>
            </Row>
          ))}
        </Body>
      </InnerWrapper>
    </Wrapper>
  );
};

export default TableTable;
